/**
 * What a re-score sends and roughly costs. Pure functions over the experiment,
 * the evaluator list and the drawer's picks, so the drawer only renders.
 *
 * The estimate is a hint, not a promise: checks are items in scope × evaluators
 * picked, judge calls the paid ones among them, priced at this experiment's own judge cost per check so far.
 * With no judge spend to go on, the cost is "Unknown", never $0.
 */

import type { Evaluator } from "@/features/evaluators/types";
import type { EvaluatorChoice, ExperimentEvaluator, ExperimentSummary, RescoreScope } from "../types";

/** The newest version of each evaluator key in the list. */
export function latestByKey(evaluators: Evaluator[]): Map<string, Evaluator> {
  const out = new Map<string, Evaluator>();
  for (const e of evaluators) {
    const seen = out.get(e.key);
    if (!seen || (e.version ?? 1) > (seen.version ?? 1)) out.set(e.key, e);
  }
  return out;
}

/** "v2 → v3" when a newer version exists, "v2" otherwise, "" when unknown. */
export function versionLabel(used: number | undefined, latest: number | undefined): string {
  if (used === undefined) return latest === undefined ? "" : `v${latest}`;
  if (latest !== undefined && latest > used) return `v${used} → v${latest}`;
  return `v${used}`;
}

/** The experiment's evaluators at their latest version, plus any added ones. */
export function rescoreChoices(
  current: ExperimentEvaluator[],
  keptKeys: Set<string>,
  latest: Map<string, Evaluator>,
  added: Evaluator[],
): EvaluatorChoice[] {
  const kept = current
    .filter((ev) => keptKeys.has(ev.key))
    .map((ev): EvaluatorChoice => {
      const newest = latest.get(ev.key);
      const version = newest?.version ?? ev.version;
      return { evaluator_id: newest?.id ?? ev.id, ...(version !== undefined ? { version } : {}) };
    });
  const extra = added.map((e): EvaluatorChoice => ({
    evaluator_id: e.id,
    ...(e.version !== undefined ? { version: e.version } : {}),
  }));
  return [...kept, ...extra];
}

/** Items a scope covers: scored outputs, the most errors any picked evaluator had, or the selection. */
export function itemsInScope(
  e: ExperimentSummary,
  scope: RescoreScope,
  keys: string[],
  selected: number,
): number {
  if (scope === "selected") return selected;
  if (scope === "all") return e.counts.done;
  return Math.max(0, ...keys.map((k) => e.aggregate?.evaluators[k]?.errors ?? 0));
}

/** This experiment's judge cost per paid check, or null when there is nothing to go on. */
export function costPerJudgeCall(e: ExperimentSummary): number | null {
  const paid = e.evaluators.filter((ev) => ev.is_paid).length;
  const checks = paid * e.counts.done;
  if (checks === 0 || e.spend.evaluator_usd <= 0) return null;
  return e.spend.evaluator_usd / checks;
}

/**
 * What the drawer's footer states: checks (items in scope × evaluators picked),
 * judge calls (the paid share of those) and their rough cost — 0 when there are
 * no judge calls, null when there is no judge spend to price them on.
 */
export function estimate(e: ExperimentSummary, picked: number, paidPicked: number, inScope: number) {
  const calls = paidPicked * inScope;
  const perCall = costPerJudgeCall(e);
  return {
    checks: picked * inScope,
    calls,
    cost: calls === 0 ? 0 : perCall === null ? null : perCall * calls,
  };
}

/** Why Run is off, or null. The server enforces the same (422 while running). */
export function rescoreBlocker(
  e: ExperimentSummary,
  busy: boolean,
  picked: number,
  inScope: number,
): string | null {
  if (["running", "stopping", "aggregating"].includes(e.status))
    return "Wait for the run to finish before re-scoring.";
  if (e.status === "draft") return "Run the experiment first; there are no outputs to score yet.";
  if (busy) return "A re-score is already running.";
  if (picked === 0) return "Pick at least one evaluator.";
  if (inScope === 0) return "No items in this scope.";
  return null;
}
